import { Rectangle }     from "../../../types";
import { chooseService } from "../../../utils";
import MyDate            from "../../../utils/MyDate";

export type TimelineOptionsType = {
  time: any,
  duration: number,
  timestrips: any,
  scaleMarkInterval: number,
  scaleTimeMarkInterval: number,
  shiftDate: number
};

const renderLine = (ctx: CanvasRenderingContext2D, x: number, fromY: number, toY: number, color: string) => {
  ctx.beginPath();
  ctx.moveTo(x, fromY);
  ctx.lineTo(x, toY);
  ctx.strokeStyle = color;
  ctx.stroke();
};

const renderTime = (ctx: CanvasRenderingContext2D, text: string, x: number, y: number, checkedPane: number | null) => {
  ctx.font = "40px Arial";
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = 'center';
  const {width} = ctx.measureText(text);

  const fromY = checkedPane ? 95 : 5;
  const toY = checkedPane ? 30 : 70;
  const textY = checkedPane ? 45 : 85;

  renderLine(ctx, x, fromY, toY, '#cccccc');
  ctx.fillText(text, x, textY, width / 4);
};

const renderCenterTime = (
  ctx: CanvasRenderingContext2D, time: number, x: number, y: number, isLocalTime: boolean
) => {
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = 'center';
  const textTime = getTime(time, isLocalTime);
  const textDate = getData(time);
  const {width} = ctx.measureText(textTime);
  ctx.font = "40px Arial";
  ctx.fillText(textDate, x + width / 5, y - 10, width / 4);
  ctx.font = "65px Arial";
  ctx.fillText(textTime, x - width / 5, y, width / 2);
};

export const renderRect = (ctx: CanvasRenderingContext2D, {x, y, width, height}: Rectangle, type: number) => {
  const gradient = ctx.createLinearGradient(x, y, x, y + height);

  let startColor, endColor: string;

  switch (type) {
    case 1: {
      startColor = "#4a771f"; // Green standart recording
      endColor = "#00633F";
      break;
    }
    case 2: {
      startColor = "#5f1113"; // Red alarm recording
      endColor = "#bf1f26";
      break;
    }
    case 3: {
      startColor = "#ffffff"; // black no recording(uncertain)
      endColor = "#000000";
      break;
    }
    case 5: {
      startColor = "#ffffff"; // black no recording
      endColor = "#000000";
      break;
    }
    case 6: {
      startColor = "#ffffff"; // White exporting
      endColor = "#ffffff";
      break;
    }
    default:
      return;
  }

  gradient.addColorStop(0, startColor);
  gradient.addColorStop(.99, endColor);
  gradient.addColorStop(1, startColor);
  ctx.fillStyle = endColor;

  const shift = 5;

  ctx.fillRect(x, y + shift, width, height - shift);
  ctx.globalAlpha = 1;
};

const getTime = (time: number, isLocalTime: boolean) => {
  const date = new MyDate(time);
  return isLocalTime ? date.toLocaleTimeString() : date.to24TimeString();
};

const getData = (time: number) => {
  const date = new Date(time);
  return date.toLocaleDateString();
};

export const fillTimeStrips = (ctx: CanvasRenderingContext2D,
                               timeline: TimelineOptionsType,
                               playbackCams: any[],
                               checkedCamID: null | string,
                               expandedVideoLayout: boolean,
                               checkedPane: number | null
) => {
  // const cams = playbackCams.filter((camId) => camId);
  const len = playbackCams.length;

  !expandedVideoLayout && playbackCams.forEach((camId: string | null, i: number) => {
    const y = i * (100 / len);
    const height = 100 / len;
    const alpha = checkedPane === i ? 1 : 0.5;
    if (len === 1 && checkedCamID !== camId) return;
    if (len === 1 && checkedPane === 1) {
      camId && fillTimeStrip(ctx, timeline, checkedPane, y, height, 1, playbackCams);
      return;
    }
    camId && fillTimeStrip(ctx, timeline, i, y, height, alpha, playbackCams);
  });

  if (expandedVideoLayout && checkedCamID && typeof checkedPane === 'number') {
    fillTimeStrip(ctx, timeline, checkedPane, 0, 100, 1, playbackCams)
  }
};

export const fillTimeStrip = (ctx: CanvasRenderingContext2D,
                              timeline: TimelineOptionsType,
                              pane: number,
                              y: number,
                              height: number,
                              alpha: number,
                              playbackCams: any[]
) => {
  const {time, duration, timestrips, shiftDate} = timeline;
  const date = time - shiftDate;
  const startTime = date - duration / 2;
  const strips = timestrips[pane];

  strips && strips.forEach(({high, low, type}: any) => {
    const x = (low - startTime) / duration * ctx.canvas.width;
    const width = (high - low) / duration * ctx.canvas.width;
    ctx.globalAlpha = alpha;
    chooseService(
      () => {
        renderRect(ctx, {x, y: y, width, height: height}, type);
      },
      () => {
        if (type === 0) {
          renderBookmarkFlag(ctx, {low, high}, timeline, pane, playbackCams, false)
        } else {
          renderRect(ctx, {x, y: y, width, height: height}, type);
        }
      }
    )
    ctx.globalAlpha = 1;
  })
};

const renderTimelineReticle = (ctx: CanvasRenderingContext2D, timeline: TimelineOptionsType, checkedPane: number | null) => {
  const {width} = ctx.canvas;
  const {time, duration, scaleMarkInterval, shiftDate} = timeline;

  const date = time - shiftDate;
  const startTime = date - duration / 2;
  const shift = date % scaleMarkInterval;
  const amount = duration / scaleMarkInterval;

  for (let i = 0; i < amount + 1; i++) {
    const timeTick = startTime - shift + scaleMarkInterval * i;
    const x = (timeTick - startTime) / duration * width;
    const fromY = checkedPane ? 95 : 5;
    const toY = checkedPane ? 80 : 20;
    renderLine(ctx, x, fromY, toY, '#ffffff');
  }
};

const renderTimelineTime = (
  ctx: CanvasRenderingContext2D,
  timeline: TimelineOptionsType,
  checkedPane: number | null,
  isLocalTime: boolean
) => {
  const {width} = ctx.canvas;
  const {time, duration, scaleTimeMarkInterval, shiftDate} = timeline;

  const date = time - shiftDate;
  const startTime = date - duration / 2;
  const shift = date % scaleTimeMarkInterval;
  const amount = duration / scaleTimeMarkInterval;

  for (let i = 0; i < amount + 1; i++) {
    const timeTick = startTime - shift + scaleTimeMarkInterval * i;
    const x = (timeTick - startTime) / duration * width;
    let alpha = Math.abs(x - width / 2) / (0.3 * width);
    alpha = alpha < 0.3 ? 0 : alpha;
    ctx.globalAlpha = alpha > 1 ? 1 : alpha;
    renderTime(ctx, getTime(timeTick, isLocalTime), x, 30, checkedPane);
  }

  ctx.globalAlpha = 1;
  const timeY = checkedPane ? 55 : 95;
  renderCenterTime(ctx, date, width / 2, timeY, isLocalTime);
};

const renderCenterNeedle = (ctx: CanvasRenderingContext2D, checkedPane: number | null) => {
  const x = ctx.canvas.width / 2;
  const dX = 5;
  const startY = checkedPane ? 100 : 0;

  ctx.beginPath();
  ctx.fillStyle = '#ffffff';
  ctx.moveTo(x - dX, startY);
  ctx.lineTo(x, Math.abs(startY - 20));
  ctx.lineTo(x + dX, startY);
  ctx.lineTo(x - dX, startY);
  ctx.fill();
};

const renderBookmarkFlag = (
  ctx: CanvasRenderingContext2D,
  {low, high}: { low: number, high: number },
  timeline: TimelineOptionsType,
  checkedPane: number | null,
  playbackCams: any[],
  isExport: boolean
) => {

  const len = playbackCams.length;
  const coef = len > 1 ? 1 : 1.5;

  const {time, duration, shiftDate} = timeline;
  const date = time - shiftDate;
  const startTime = date - duration / 2;
  const y = len > 1 ? checkedPane ? 60 : 10 : (100 - 30 * coef) / 2;

  const xStart = (low - startTime) / duration * ctx.canvas.width;
  const width = (high - low) / duration * ctx.canvas.width;
  const center = xStart + width / 2;

  ctx.beginPath();
  ctx.moveTo(center - 1, y);
  ctx.lineTo(center - 1, y + 30 * coef);
  ctx.lineTo(center + 1, y + 30 * coef);
  ctx.lineTo(center + 1, y + 15 * coef);
  ctx.lineTo(center + 10 * coef, y + 15 * coef);
  ctx.lineTo(center + 5 * coef, y + 7.5 * coef);
  ctx.lineTo(center + 10 * coef, y);
  ctx.lineTo(center - 1, y);
  ctx.closePath();
  ctx.fillStyle = isExport ? '#f9b233' : '#ffffff';
  ctx.fill();
}

export const renderTimeLine = (canvas: HTMLCanvasElement | null,
                               timeline: TimelineOptionsType,
                               checkedCamID: string | null,
                               playbackCams: any[],
                               checkedPane: number | null,
                               expandedVideoLayout: boolean,
                               isLocalTime: boolean,
                               renderExports?: { low: number, high: number } | null
) => {
  if (canvas) {
    const context = canvas.getContext("2d");

    if (context) {
      context.clearRect(0, 0, context.canvas.width, context.canvas.height);
      fillTimeStrips(context, timeline, playbackCams, checkedCamID, expandedVideoLayout, checkedPane);
      if (renderExports) {
        fillExportsLine(context, renderExports, timeline);
      }
      renderTimelineReticle(context, timeline, checkedPane);
      renderTimelineTime(context, timeline, checkedPane, isLocalTime);
      chooseService(
        () => null,
        () => {
          if (renderExports) {
            renderBookmarkFlag(context, renderExports, timeline, checkedPane, playbackCams, true);
          }
        }
      )
      renderCenterNeedle(context, checkedPane);
    }
  }
};

export const fillExportsLine = (
  ctx: CanvasRenderingContext2D,
  {low, high}: { low: number, high: number },
  timeline: TimelineOptionsType
) => {

  const {time, duration, shiftDate} = timeline;
  const date = time - shiftDate;
  const startTime = date - duration / 2;

  const x = (low - startTime) / duration * ctx.canvas.width;
  const width = (high - low) / duration * ctx.canvas.width;
  ctx.globalAlpha = 0.5;
  renderRect(ctx, {x, y: 0, width, height: 100}, 6);
  ctx.globalAlpha = 1;
};